(() => {
  if (customElements.get('paws-peps-cart-drawer')) return;

  const SELECTORS = {
    cartTriggers: [
      'a[href="/cart"]',
      'a[href$="/cart"]',
      '.header__icon--cart',
      '[aria-controls="CartDrawer"]',
      '[data-cart-toggle]',
      '[data-cart-drawer-trigger]',
      '[data-cart-icon-bubble]'
    ].join(','),
    addForms: 'form[action*="/cart/add"]',
    focusable:
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const ICONS = {
    shipping: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg>`,
    discount: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5l10 10-5 5L4 9z"></path><circle cx="8" cy="8" r="1"></circle></svg>`,
    gift: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h16v10H4zM3 7h18v3H3zM12 7v13"></path><path d="M12 7H8.5A2.5 2.5 0 1 1 11 4.5L12 7z"></path><path d="M12 7h3.5A2.5 2.5 0 1 0 13 4.5L12 7z"></path></svg>`,
    trash: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"></path></svg>`
  };

  const escapeHTML = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  class PawsPepsCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.busy = false;
      this.bound = false;
      this.lastActiveElement = null;
      this.productCache = new Map();
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      this.documentSubmitHandler = this.handleDocumentSubmit.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.externalCartHandler = this.handleExternalCartEvent.bind(this);
    }

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.panel = this.querySelector('.ppcd__panel');
      this.itemsContainer = this.querySelector('[data-ppcd-items]');
      this.emptyState = this.querySelector('[data-ppcd-empty]');
      this.footer = this.querySelector('[data-ppcd-footer]');
      this.errorBox = this.querySelector('[data-ppcd-error]');
      this.loading = this.querySelector('[data-ppcd-loading]');
      this.countElements = this.querySelectorAll('[data-ppcd-count]');
      this.regularTotalRow = this.querySelector('[data-ppcd-regular-row]');
      this.regularTotalElement = this.querySelector('[data-ppcd-regular-total]');
      this.subtotalElement = this.querySelector('[data-ppcd-subtotal]');
      this.savingsRow = this.querySelector('[data-ppcd-savings-row]');
      this.savingsElement = this.querySelector('[data-ppcd-savings]');
      this.checkoutTotal = this.querySelector('[data-ppcd-checkout-total]');
      this.rewardMessage = this.querySelector('[data-ppcd-reward-message]');
      this.progressFill = this.querySelector('[data-ppcd-progress-fill]');
      this.goalsContainer = this.querySelector('[data-ppcd-goals]');
      this.config = this.readConfig();

      const hostStyle = window.getComputedStyle(this);
      const panelStyle = this.panel ? window.getComputedStyle(this.panel) : null;
      const stylesReady =
        hostStyle.position === 'fixed' &&
        panelStyle &&
        panelStyle.position === 'absolute';

      if (!stylesReady) {
        console.error(
          '[Paws & Peps cart] Stylesheet did not load. The drawer was kept hidden to protect the page layout.'
        );
        return;
      }

      this.style.visibility = 'visible';

      this.querySelectorAll('[data-ppcd-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.itemsContainer?.addEventListener('click', (event) => {
        const actionButton = event.target.closest('[data-ppcd-action]');
        if (!actionButton) return;
        this.handleItemAction(actionButton);
      });

      document.addEventListener('click', this.documentClickHandler, true);
      document.addEventListener('submit', this.documentSubmitHandler, true);
      document.addEventListener('keydown', this.keydownHandler);

      [
        'cart:updated',
        'cart:refresh',
        'product:added',
        'ajaxProduct:added',
        'theme:cart:updated'
      ].forEach((eventName) => {
        document.addEventListener(eventName, this.externalCartHandler);
      });

      this.refresh({ silent: true });

      if (this.dataset.testMode === 'true' && window.Shopify?.designMode) {
        window.setTimeout(() => this.open(), 200);
      }

      window.PawsPepsCartDrawer = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh()
      };
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler, true);
      document.removeEventListener('submit', this.documentSubmitHandler, true);
      document.removeEventListener('keydown', this.keydownHandler);
      [
        'cart:updated',
        'cart:refresh',
        'product:added',
        'ajaxProduct:added',
        'theme:cart:updated'
      ].forEach((eventName) => {
        document.removeEventListener(eventName, this.externalCartHandler);
      });
    }

    readConfig() {
      try {
        const raw = this.querySelector('[data-ppcd-config]')?.textContent || '{}';
        const config = JSON.parse(raw);
        config.goals = (config.goals || [])
          .filter((goal) => goal.enabled && Number(goal.amount) > 0)
          .sort((a, b) => Number(a.amount) - Number(b.amount));
        config.imageOverrides = (config.imageOverrides || [])
          .filter((entry) => entry.handle && entry.src);
        return config;
      } catch (error) {
        console.error('[Paws & Peps cart] Invalid config', error);
        return { goals: [], imageOverrides: [], showRewards: false };
      }
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest(SELECTORS.cartTriggers);
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }

    handleDocumentSubmit(event) {
      if (this.dataset.interceptAdd !== 'true') return;
      const form = event.target.closest(SELECTORS.addForms);
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addFromForm(form, event.submitter);
    }

    handleExternalCartEvent() {
      this.refresh({
        silent: false,
        open: this.dataset.openAfterAdd === 'true'
      });
    }

    handleKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.panel.querySelectorAll(SELECTORS.focusable))
        .filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      this.lastActiveElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ppcd-lock');
      document.body.classList.add('ppcd-lock');
      this.panel?.focus({ preventScroll: true });
      this.refresh({ silent: true });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ppcd-lock');
      document.body.classList.remove('ppcd-lock');

      if (this.lastActiveElement instanceof HTMLElement) {
        this.lastActiveElement.focus({ preventScroll: true });
      }
    }

    async addFromForm(form, submitter) {
      if (this.busy) return;
      this.setBusy(true);
      this.clearError();
      if (submitter) submitter.disabled = true;

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'This product could not be added to the cart.');
        }
        await this.refresh({ silent: true });
        if (this.dataset.openAfterAdd === 'true') this.open();
        document.dispatchEvent(new CustomEvent('paws-peps:cart-added', { detail: result }));
      } catch (error) {
        this.showError(error.message);
        this.open();
      } finally {
        if (submitter) submitter.disabled = false;
        this.setBusy(false);
      }
    }

    async refresh({ silent = false, open = false } = {}) {
      if (!silent) this.setBusy(true);
      try {
        const response = await fetch(this.dataset.cartUrl, {
          headers: { Accept: 'application/json' }
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          throw new Error(cart.description || 'Unable to load the cart.');
        }
        await this.enrichCartPricing(cart);
        this.render(cart);
        if (open) this.open();
      } catch (error) {
        this.showError(error.message);
      } finally {
        if (!silent) this.setBusy(false);
      }
    }

    async changeLine(key, quantity) {
      if (this.busy || !key) return;
      this.setBusy(true);
      this.clearError();

      try {
        const response = await fetch(this.dataset.cartChangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: Math.max(0, Number(quantity) || 0)
          })
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          throw new Error(cart.description || 'Unable to update the cart.');
        }
        await this.enrichCartPricing(cart);
        this.render(cart);
        document.dispatchEvent(new CustomEvent('paws-peps:cart-updated', { detail: cart }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setBusy(false);
      }
    }

    handleItemAction(button) {
      const key = button.dataset.key;
      const current = Number(button.dataset.quantity || 0);
      const action = button.dataset.ppcdAction;

      if (action === 'increase') {
        this.changeLine(key, current + 1);
      } else if (action === 'decrease') {
        this.changeLine(key, Math.max(0, current - 1));
      } else if (action === 'remove') {
        this.changeLine(key, 0);
      }
    }

    async getProductData(handle) {
      if (!handle) return null;

      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      })
        .then(async (response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .catch(() => null);

      this.productCache.set(handle, request);
      return request;
    }

    async enrichCartPricing(cart) {
      if (!Array.isArray(cart?.items) || !cart.items.length) return;

      const handles = [...new Set(cart.items.map((item) => item.handle).filter(Boolean))];
      const products = await Promise.all(handles.map((handle) => this.getProductData(handle)));
      const productMap = new Map();

      handles.forEach((handle, index) => {
        if (products[index]) productMap.set(handle, products[index]);
      });

      cart.items.forEach((item) => {
        const product = productMap.get(item.handle);
        const variant = product?.variants?.find(
          (entry) => String(entry.id) === String(item.variant_id)
        );

        const compareAt = Number(variant?.compare_at_price || 0);
        const variantPrice = Number(variant?.price || 0);

        item._ppcd_compare_at_price = compareAt;
        item._ppcd_variant_price = variantPrice;
      });
    }

    getItemPricing(item) {
      const quantity = Math.max(1, Number(item.quantity || 1));
      const finalUnit = Number(
        item.final_price ??
        item.discounted_price ??
        item.price ??
        item._ppcd_variant_price ??
        0
      );
      const finalLine = Number(
        item.final_line_price ??
        item.line_price ??
        finalUnit * quantity
      );
      const cartOriginalLine = Number(
        item.original_line_price ??
        (Number(item.original_price || 0) * quantity) ??
        finalLine
      );
      const compareAtLine = Number(item._ppcd_compare_at_price || 0) * quantity;
      const regularLine = Math.max(finalLine, cartOriginalLine, compareAtLine);
      const saving = Math.max(0, regularLine - finalLine);
      const discountPercent = regularLine > 0
        ? Math.round((saving / regularLine) * 100)
        : 0;

      return {
        regularLine,
        finalLine,
        saving,
        discountPercent,
        hasDiscount: saving > 0
      };
    }

    render(cart) {
      this.cart = cart;
      this.clearError();

      const itemCount = Number(cart.item_count || 0);
      this.countElements.forEach((element) => {
        element.textContent = itemCount === 1 ? '• 1 item' : `• ${itemCount} items`;
      });

      const hasItems = Array.isArray(cart.items) && cart.items.length > 0;
      this.itemsContainer.hidden = !hasItems;
      this.emptyState.hidden = hasItems;
      this.footer.hidden = !hasItems;

      if (hasItems) {
        this.itemsContainer.innerHTML = cart.items.map((item) => this.renderItem(item)).join('');
      } else {
        this.itemsContainer.innerHTML = '';
      }

      const itemTotals = (cart.items || []).reduce(
        (totals, item) => {
          const pricing = this.getItemPricing(item);
          totals.regular += pricing.regularLine;
          totals.final += pricing.finalLine;
          return totals;
        },
        { regular: 0, final: 0 }
      );

      const finalTotal = Number(cart.total_price || itemTotals.final || 0);
      const regularTotal = Math.max(
        finalTotal,
        itemTotals.regular,
        Number(cart.original_total_price || 0)
      );
      const savings = Math.max(0, regularTotal - finalTotal);

      if (this.regularTotalRow && this.regularTotalElement) {
        this.regularTotalRow.hidden = savings <= 0;
        this.regularTotalElement.textContent = this.formatMoney(regularTotal);
      }

      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(finalTotal);
      }

      if (this.checkoutTotal) {
        this.checkoutTotal.textContent = this.formatMoney(finalTotal);
      }

      if (this.savingsRow && this.savingsElement) {
        this.savingsRow.hidden = savings <= 0;
        this.savingsElement.textContent = `-${this.formatMoney(savings)}`;
      }

      this.renderRewards(cart);
      this.updateCartBubbles(itemCount);
    }

    renderItem(item) {
      const override = this.config.imageOverrides.find((entry) => entry.handle === item.handle);
      const image = override?.src || item.featured_image?.url || item.image || '';
      const imageAlt = item.featured_image?.alt || item.product_title || item.title || '';

      const pricing = this.getItemPricing(item);
      const originalLine = pricing.regularLine;
      const finalLine = pricing.finalLine;
      const saving = pricing.saving;
      const hasCompare = pricing.hasDiscount;
      const discountPercent = pricing.discountPercent;
      const variant = this.getVariantText(item);
      const quantity = Number(item.quantity || 1);
      const key = escapeHTML(item.key);
      const productUrl = escapeHTML(item.url || '#');

      return `
        <article class="ppcd__item" data-key="${key}">
          <a class="ppcd__media" href="${productUrl}">
            ${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(imageAlt)}" loading="eager">` : ''}
          </a>
          <div class="ppcd__item-main">
            <div class="ppcd__item-top">
              <a class="ppcd__item-title" href="${productUrl}">${escapeHTML(item.product_title || item.title)}</a>
              <button class="ppcd__remove" type="button" aria-label="Remove ${escapeHTML(item.product_title || item.title)}" data-ppcd-action="remove" data-key="${key}" data-quantity="${quantity}">${ICONS.trash}</button>
            </div>
            ${variant ? `<p class="ppcd__variant">${escapeHTML(variant)}</p>` : '<span></span>'}
            <div class="ppcd__item-bottom">
              <div class="ppcd__quantity" aria-label="Quantity selector">
                <button class="ppcd__quantity-button" type="button" aria-label="Decrease quantity" data-ppcd-action="decrease" data-key="${key}" data-quantity="${quantity}">−</button>
                <span class="ppcd__quantity-value" aria-live="polite">${quantity}</span>
                <button class="ppcd__quantity-button" type="button" aria-label="Increase quantity" data-ppcd-action="increase" data-key="${key}" data-quantity="${quantity}">+</button>
              </div>
              <div class="ppcd__price">
                ${hasCompare ? `<span class="ppcd__compare-price">${this.formatMoney(originalLine)}</span>` : ''}
                <span class="ppcd__final-price">${this.formatMoney(finalLine)}</span>
                ${saving > 0 ? `<span class="ppcd__saving"><strong>${discountPercent}% OFF</strong><span>•</span>You save ${this.formatMoney(saving)}</span>` : ''}
              </div>
            </div>
          </div>
        </article>
      `;
    }

    getVariantText(item) {
      const options = Array.isArray(item.options_with_values)
        ? item.options_with_values
            .filter((option) => option.value && option.value !== 'Default Title')
            .map((option) => `${option.name}: ${option.value}`)
        : [];
      return options.join(' / ');
    }

    renderRewards(cart) {
      if (!this.config.showRewards || !this.rewardMessage || !this.goalsContainer || !this.progressFill) return;

      const goals = this.config.goals || [];
      const subtotal = Number(cart.total_price || 0);

      if (!goals.length) {
        this.rewardMessage.textContent = '';
        this.goalsContainer.innerHTML = '';
        this.progressFill.style.width = '0%';
        return;
      }

      const maxGoal = Math.max(...goals.map((goal) => Number(goal.amount)));
      const progress = Math.min(100, Math.max(0, (subtotal / maxGoal) * 100));
      this.progressFill.style.width = `${progress}%`;

      const nextGoal = goals.find((goal) => subtotal < Number(goal.amount));

      if (!nextGoal) {
        this.rewardMessage.textContent = this.config.successMessage || "Congrats! You've unlocked all promotions!";
      } else {
        const remaining = Math.max(0, Number(nextGoal.amount) - subtotal);
        this.rewardMessage.textContent = (this.config.progressMessage || "You're [amount] away from [goal]!")
          .replace('[amount]', this.formatMoney(remaining))
          .replace('[goal]', nextGoal.label);
      }

      this.goalsContainer.innerHTML = goals.map((goal) => {
        const unlocked = subtotal >= Number(goal.amount);
        return `<div class="ppcd__goal ${unlocked ? 'is-unlocked' : ''}"><span class="ppcd__goal-icon">${ICONS[goal.icon] || ICONS.gift}</span><span class="ppcd__goal-label">${escapeHTML(goal.label)}</span></div>`;
      }).join('');
    }

    updateCartBubbles(count) {
      const selectors = [
        '.cart-count-bubble',
        '[data-cart-count]',
        '[data-cart-count-bubble]',
        '.header__cart-count',
        '.cart-count'
      ];

      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        const numberElement = element.querySelector('[aria-hidden="true"], span') || element;
        numberElement.textContent = String(count);
        element.hidden = count <= 0;
        element.classList.toggle('is-empty', count <= 0);
      });
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'CAD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
          style: 'currency',
          currency
        }).format(Number(cents || 0) / 100);
      } catch {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      if (this.loading) this.loading.hidden = !isBusy;
      this.querySelectorAll('.ppcd__quantity-button, .ppcd__remove, .ppcd__checkout').forEach((element) => {
        if ('disabled' in element) element.disabled = isBusy;
        element.setAttribute('aria-disabled', String(isBusy));
      });
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message || 'Something went wrong.';
      this.errorBox.hidden = false;
    }

    clearError() {
      if (!this.errorBox) return;
      this.errorBox.textContent = '';
      this.errorBox.hidden = true;
    }
  }

  customElements.define('paws-peps-cart-drawer', PawsPepsCartDrawer);
})();
